import type { InAppMessage } from '@braze/web-sdk';

import log, { logError, logWarn } from '~/common/utils/log';
import type { User } from '~/services/users/types';
import { isBrowserFn } from '~/utils/browser';
import { isEoBrand } from '~/utils/isEoBrand';
import { isLocalEnvironment } from '~/utils/isLocalEnvironment';

import { reportEvent } from '../events';

import renderSlideUpMessage from './slideUpMessage';
import type { BrazeInstance } from './types';
import { isSlideUpMessage } from './types';

const isChunkLoadError = (error: unknown): error is Error =>
  error instanceof Error && error.name === 'ChunkLoadError';

class BrazeSingleton {
  private static instance: BrazeInstance;

  private static activeUserId: string;

  // ------------------ !!! IMPORTANT !!! ------------------
  // If this ENV variable is not true, no events will be sent to Braze.
  // This means we have to enable Segment -> Braze destination mapping for campaigns to work
  // And disable the same destination in Segment if "true", to avoid double sending events
  // ------------------ !!! IMPORTANT !!! ------------------
  private static isEventsEnabled = process.env.NEXT_PUBLIC_BRAZE_SEND_EVENTS === 'true';

  private static async initBraze() {
    if (!process.env.NEXT_PUBLIC_BRAZE_SDK_KEY || !process.env.NEXT_PUBLIC_BRAZE_SDK_ENDPOINT) {
      logError('Braze: No SDK key or endpoint found!');
      return;
    }

    // Never initialize Braze for EO brands (unified login)
    if (isEoBrand()) {
      return;
    }

    try {
      const brazeInstance = await import(
        /*
         * Braze SDK is broken in SSR, so we need to import it dynamically
         * Using webpackExports magic with to get around "ReferenceError: navigator is not defined"
         * Do nor mess with the async methods unless you know what you're doing!
         */
        /* webpackExports: ["changeUser", "destroy", "getUser", "initialize", "isInitialized", "logCustomEvent", "logInAppMessageImpression", "logInAppMessageClick", "openSession", "setLogger", "subscribeToInAppMessage", "showInAppMessage"] */
        '@braze/web-sdk'
      );

      await brazeInstance.initialize(process.env.NEXT_PUBLIC_BRAZE_SDK_KEY, {
        baseUrl: process.env.NEXT_PUBLIC_BRAZE_SDK_ENDPOINT,
        enableLogging: isLocalEnvironment(),
        appVersion: process.env.VERSION,

        // TODO: Figure font-awesome implementation once we need to support icons
        doNotLoadFontAwesome: true,
        allowUserSuppliedJavascript: true,
        noCookies: true,
      });

      // Subscribe to receive in-app messages
      await brazeInstance.subscribeToInAppMessage((inAppMessage: InAppMessage) => {
        // Control group messages should always be "shown" by default Braze UI
        // This will log an impression and not show a visible message
        if (inAppMessage.isControl) {
          return brazeInstance.showInAppMessage(inAppMessage);
        }

        // Use custom render method for slide-up messages
        if (isSlideUpMessage(inAppMessage)) {
          return renderSlideUpMessage(brazeInstance, inAppMessage);
        }

        // Use default Braze UI to show all other messages
        // TODO: Handle modal messages with internal renderer as well
        brazeInstance.showInAppMessage(inAppMessage);

        // Analytics event for all other in-app messages
        if (inAppMessage.extras && Object.keys(inAppMessage.extras).length > 0) {
          reportEvent('Braze > InApp Message Shown', {
            ...inAppMessage.extras,
          });
        }
      });

      brazeInstance.setLogger(log);
      brazeInstance.openSession();

      return brazeInstance;
    } catch (error) {
      if (isChunkLoadError(error)) {
        logWarn('Braze: SDK failed to load', error);
        return;
      }

      logError(error);
    }
  }

  private static async getInstance() {
    if (!this.instance && isBrowserFn()) {
      const instance = await this.initBraze();
      this.instance = instance;
    }

    return this.instance;
  }

  public static async start() {
    await this.getInstance();

    return true;
  }

  public static isInitialized() {
    return this.instance && this.instance.isInitialized();
  }

  public static async setUser(user: Partial<User>) {
    if (!user) return;

    const { id: userId, email, firstName, lastName } = user;
    if (!userId || userId === this.activeUserId) return;
    const instance = await this.getInstance();

    this.activeUserId = userId;
    await instance?.changeUser(userId);

    // Identify
    if (instance && typeof instance.getUser === 'function') {
      const brazeUser = instance.getUser();

      if (email) brazeUser?.setEmail(email);
      if (firstName) brazeUser?.setFirstName(firstName);
      if (lastName) brazeUser?.setLastName(lastName);
    }

    return true;
  }

  public static async logEvent(name: string, properties: Record<string, unknown>) {
    // Don't send events if not enabled via environment variable
    if (!this.isEventsEnabled) return;

    // Don't send events if the user is not logged in
    if (!this.activeUserId) return;

    const instance = await this.getInstance();
    return instance?.logCustomEvent(name, properties);
  }

  public static async stop() {
    const instance = await this.getInstance();

    this.activeUserId = null;

    if (instance && typeof instance.destroy === 'function') {
      return instance.destroy();
    }
  }
}

export default BrazeSingleton;
